/*
 * Listas de productos de proveedores.
 *
 * Lista de inventario que muestra proveedores y sus productos registrados.
 *
 * Created on Tue Jul 18 2023 by José Luis Chávez Ayala.
 *
 * Copyright (c) 2023 Turn My App.
 */

<template>
  <div>
    <!-- #region::Skeleton -->
    <template v-if="isLoadingSuppliersData">
      <BasicSkeleton height="200px" />
      <BasicSkeleton height="100px" />
    </template>
    <!-- #endregion::Skeleton -->

    <template v-else>

      <!-- #region::Multiple supplier selector -->
      <SupplierSelector
        v-if="$ability.can('filter columns_supplier', 'Reports')"
        @option:selected="onSelectedTab"
        @option:deselected="onCloseTab"
      />
      <!-- #endregion::Multiple supplier selector -->

      <!-- #region::Products tabs -->
      <b-tabs
        v-if="$ability.can('read without_suppliers', 'Reports')"
        v-model="selectedIndex"
        @activate-tab="activateTab"
        @changed="onTabChanged"
      >
        <b-tab
          v-for="tab in tabs"
          :key="tab.value"
          lazy
          :title="tab.title"
          @click="onSelectedTab(tab.title)"
          @on:close-tab="onCloseTab"
        >
          <SuppliersList
            :title="tab.title"
            :show-supplier-components="tab.value === 'all'"
            :without-supplier-term="tab.value === 'withoutSupplier'"
            @on:close-tab="onCloseTab"
          />
        </b-tab>
        <!-- #endregion::Products tabs -->

      </b-tabs>

      <template v-else>
        <!-- #region begin:title -->
        <b-form-row>
          <b-col
            cols="12"
            class="mb-1"
          >
            <h3>Proveedores</h3>
          </b-col>
        </b-form-row>
        <!-- #endregion end:title -->

        <SuppliersList
          title="Todos"
          :show-supplier-components="true"
          :without-supplier-term="false"
        />
      </template>

    </template>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import {
  BTabs, BTab, BCol, BFormRow,
} from 'bootstrap-vue'

import SupplierSelector from '@/modules/reports/suppliers/components/SupplierSelector.vue'
import SuppliersList from '@/modules/reports/suppliers/components/SuppliersList.vue'

import BasicSkeleton from '@/components/cards/BasicSkeleton.vue'
import getError from '@/helpers/ErrorsHandler'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BTabs,
    BTab,
    BCol,
    BFormRow,
    BasicSkeleton,
    SuppliersList,
    SupplierSelector,
  },
  data() {
    return {
      selectedIndex: 0,
      isLoadingSuppliersData: false,
    }
  },
  computed: {
    ...mapGetters({
      getTabs: 'reportsSuppliers/getTabs',
      getColumnsList: 'reportsSuppliers/getColumnsList',
      getListsParams: 'reportsSuppliers/getListsParams',
      getSelectedSuppliers: 'reportsSuppliers/getSelectedSuppliers',
      /**
       * Filtros globales
       */
      getFilteredBrand: 'filters/getFilteredBrand',
      getFilteredPerPage: 'filters/getFilteredPerPage',
      getFilteredSupplier: 'filters/getFilteredSupplier',
      getFilteredSearchTerm: 'filters/getFilteredSearchTerm',
      getFilteredSubcategory: 'filters/getFilteredSubcategory',
      getFilteredCurrentPage: 'filters/getFilteredCurrentPage',
    }),
    /**
     * Listas de pestañas
     */
    tabs: {
      get() { return this.getTabs },
    },
    /**
     * Lista de columnas
     */
    columnsList: {
      get() { return this.getColumnsList },
    },
    /**
     * Listas de parametros de filtrado de pestañas
     */
    listsParams: {
      get() { return this.getListsParams },
    },
    /**
     * Proveedores seleccionados
     */
    selectedSuppliers: {
      get() { return this.getSelectedSuppliers },
      set(value) { this.setSelectedSuppliers(value) },
    },
    /**
     * Marca filtrada (filtro en store)
     */
    filteredBrand: {
      get() { return this.getFilteredBrand },
      set(value) { this.setFilteredBrand(value) },
    },
    /**
     * Indica la cantidad de elementos por página (paginado global)
     */
    filteredPerPage: {
      get() { return this.getFilteredPerPage },
      set(value) { this.setFilteredPerPage(value) },
    },
    /**
     * Proveedor filtrado (filtro en store)
     */
    filteredSupplier: {
      get() { return this.getFilteredSupplier },
      set(value) { this.setFilteredSupplier(value) },
    },
    /**
     * Término de búsqueda filtrado (filtro en store)
     */
    filteredSearchTerm: {
      get() { return this.getFilteredSearchTerm },
      set(value) { this.setFilteredSearchTerm(value) },
    },
    /**
     * Subcategoría filtrada (filtro en store)
     */
    filteredSubcategory: {
      get() { return this.getFilteredSubcategory },
      set(value) { this.setFilteredSubcategory(value) },
    },
    /**
     * Indica la página actual (paginado global)
     */
    filteredCurrentPage: {
      get() { return this.getFilteredCurrentPage },
      set(value) { this.setFilteredCurrentPage(value) },
    },
  },

  /**
  * Hook de la creación de componente
  *
  * @summary Carga la información de marcas, proveedores y categorías para
  * los filtros de búsqueda
  */
  async created() {
    this.isLoadingSuppliersData = true

    try {
      await this.loadColumnsList()
      await this.loadBrandsWithoutPagination(true)
      await this.loadSuppliersWithoutPagination(true)
      await this.loadCategoriesWithoutPagination(true)
    } catch (error) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Error al cargar reporte',
          text: getError(error),
          variant: 'danger',
          icon: 'BellIcon',
        },
      })
    } finally {
      this.isLoadingSuppliersData = false
    }
  },

  /**
  * Hook de la eliminación de componente
  *
  * @summary Limpia toda la informacion del módulo de reportes de proveedores
  */
  beforeDestroy() {
    this.clearReportsSuppliersInfo()
  },
  methods: {
    ...mapActions({
      loadColumnsList: 'reportsSuppliers/loadColumnsList',
      removeListParams: 'reportsSuppliers/removeListParams',
      removeSelectedSupplier: 'reportsSuppliers/removeSelectedSupplier',
      loadBrandsWithoutPagination: 'brands/loadBrandsWithoutPagination',
      clearReportsSuppliersInfo: 'reportsSuppliers/clearReportsSuppliersInfo',
      loadSuppliersWithoutPagination: 'suppliers/loadSuppliersWithoutPagination',
      loadCategoriesWithoutPagination: 'categories/loadCategoriesWithoutPagination',

      /**
       * Filtros globales
       */
      setFilteredBrand: 'filters/setFilteredBrand',
      setFilteredPerPage: 'filters/setFilteredPerPage',
      setFilteredSupplier: 'filters/setFilteredSupplier',
      setFilteredSearchTerm: 'filters/setFilteredSearchTerm',
      setFilteredSubcategory: 'filters/setFilteredSubcategory',
      setFilteredCurrentPage: 'filters/setFilteredCurrentPage',
    }),

    /**
     * Evento de activación de pestaña
     *
     * @summary Establece el término de búsqueda en vacío si se selecciona la
     * pestaña de todos los productos
     * @param {Number} newTabIndex - Índice de la nueva pestaña seleccionada
     */
    activateTab(newTabIndex) {
      if (newTabIndex > 1) {
        const selectedTab = this.tabs[newTabIndex]
        this.filteredSupplier = selectedTab.title
      }
    },

    /**
    * Evento de selección de pestaña
    * @summary Establece los filtros de búsqueda de la pestaña seleccionada. Si la pestaña ya ha sido
    * seleccionada anteriormente, se establecen los filtros de búsqueda de la pestaña seleccionada, de
    * lo contrario, se establecen los filtros de búsqueda por defecto
    * @param {String} title - Título de la pestaña seleccionada
    */
    onSelectedTab(title) {
      const params = this.listsParams.find(item => item.title === title)
      this.filteredPerPage = params?.perPage || 10
      this.filteredBrand = params?.brandTerm || ''
      this.filteredStatus = params?.statusTerm || ''
      this.filteredSearchTerm = params?.searchTerm || ''
      this.filteredSupplier = params?.supplierTerm || ''
      this.filteredCurrentPage = params?.currentPage || 1
      this.filteredSubcategory = params?.categoryTerm || ''
    },

    /**
    * Selección de pestaña
    * @summary Establece el índice de la pestaña seleccionada cuando se agrega un
    * nuevo proveedor. Hace excepción con la pestaña "Sin proveedor". Evita que se
    * establezca el índice de la pestaña cuando se elimina un proveedor
    * @param {Array} currentTabs - Lista de pestañas actuales
    * @param {Array} previousTabs - Lista de pestañas anteriores
    */
    onTabChanged(currentTabs, previousTabs) {
      // Verifica que la cantidad de pestañas sea mayor a 2 y que la cantidad de pestañas anteriores
      // sea menor o igual a la cantidad de pestañas actuales para establecer el índice de la pestaña
      if (this.tabs.length > 2 && previousTabs.length > 2 && previousTabs.length <= currentTabs.length) {
        this.selectedIndex = this.tabs.length - 1
      }
    },

    /**
    * Eliminación de pestaña
    * @summary Revisa la lista de pestañas y elimina la pestaña seleccionada
    * @param {String} title - Título de la pestaña a eliminar
    */
    onCloseTab(title) {
      for (let i = 0; i < this.tabs.length; i += 1) {
        if (this.tabs[i].title === title) {
          this.tabs.splice(i, 1)

          // Se elimina el proveedor de la lista de proveedores seleccionados
          const supplier = this.selectedSuppliers.find(s => s.title === title)
          this.removeSelectedSupplier(supplier)

          // Se eliminan los parámetros de la pestaña eliminada
          this.removeListParams(title)

          // Se establecen los filtros de búsqueda de la pestaña anterior
          this.onSelectedTab(this.tabs[i - 1].title)

          // Se actualiza el índice de la pestaña seleccionada
          this.selectedIndex = i - 1
        }
      }
    },
  },
}
</script>
